import axios from 'axios';

// Use same-origin relative URL for API calls.
// Both preview and production serve frontend + backend behind the same domain.
// /api/* routes to the backend automatically via reverse proxy.
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Auto-refresh: if a request gets 401 "Token expired", try refreshing silently
let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry
        && !originalRequest.url?.includes('/auth/login')
        && !originalRequest.url?.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post('/api/auth/refresh', {}, { withCredentials: true });
        refreshQueue.forEach(p => p.resolve());
        refreshQueue = [];
        return api(originalRequest);
      } catch {
        refreshQueue.forEach(p => p.reject(error));
        refreshQueue = [];
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Vehicle Types
export const vehicleTypesApi = {
  getAll: () => api.get('/vehicle-types'),
  update: (id, data) => api.put(`/vehicle-types/${id}`, data),
};

// Parking Spaces
export const spacesApi = {
  getAll: () => api.get('/spaces'),
  regenerate: (total) => api.post(`/spaces/regenerate?total=${total}`),
};

// Check-in/Check-out
export const parkingApi = {
  checkIn: (data) => api.post('/check-in', data),
  checkOutPreview: (data) => api.post('/check-out/preview', data),
  checkOutConfirm: (data) => api.post('/check-out/confirm', data),
  calculatePrice: (spaceId) => api.get(`/calculate-price/${spaceId}`),
  voidSession: (data) => api.post('/session/void', data),
};

// Exit Undo / Correction (post-checkout error recovery)
export const exitApi = {
  undoRecent: () => api.get('/exit/undo-recent'),
  undo: (transactionId, reason) => api.post('/exit/undo', { transaction_id: transactionId, reason }),
  search: (query, hours = 24) => api.get('/exit/search', { params: { query, hours } }),
  correct: (transactionId, reason) => api.post('/exit/correct', { transaction_id: transactionId, reason }),
};

// Ticket Templates (block-based editor for entry/exit tickets)
export const templatesApi = {
  get: (kind) => api.get(`/ticket-templates/${kind}`),
  save: (kind, template) => api.put(`/ticket-templates/${kind}`, template),
  restoreDefault: (kind) => api.post(`/ticket-templates/${kind}/restore-default`),
  listVersions: (kind) => api.get(`/ticket-templates/${kind}/versions`),
  restoreVersion: (kind, versionId) => api.post(`/ticket-templates/${kind}/versions/${versionId}/restore`),
  deleteVersion: (kind, versionId) => api.delete(`/ticket-templates/${kind}/versions/${versionId}`),
};

// Service Charges (Bathroom, etc.)
export const serviceChargeApi = {
  add: (data) => api.post('/service-charge', data),
  void: (data) => api.post('/service-charge/void', data),
  get: (spaceId) => api.get(`/service-charges/${spaceId}`),
};

// Search
export const searchApi = {
  search: (query) => api.post('/search', { query }),
};

// Reprint
export const reprintApi = {
  getTicket: (ticketId) => api.get(`/reprint/${ticketId}`),
  bySpace: (spaceLabel) => api.get(`/reprint/by-space/${spaceLabel}`),
};

// Reports
export const reportsApi = {
  get: (params) => api.get('/reports', { params }),
  getDetailed: (params) => api.get('/reports/detailed', { params }),
  export: (params) => api.get('/reports/export', { params, responseType: 'blob' }),
};

// Revenue Dashboard
export const revenueApi = {
  getDashboard: () => api.get('/revenue/dashboard'),
  getLiveDailyTotal: () => api.get('/live-daily-total'),
};

// Products & Services
export const productsApi = {
  getAll: (activeOnly = false) => api.get('/products', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  deactivate: (id) => api.delete(`/products/${id}`),
};

// Audit Logs
export const auditApi = {
  getLogs: (limit = 100, action = null) => api.get(`/audit-logs`, { params: { limit, action } }),
};

// System
export const systemApi = {
  reset: () => api.post('/system/reset'),
};

export default api;
